import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { differenceInCalendarDays, format, parseISO, subDays } from "date-fns";

import { Chart } from "primereact/chart";
import { ProgressBar } from "primereact/progressbar";
import { Skeleton } from "primereact/skeleton";
import "chart.js/auto";

import { useDashboardViewModel } from "../../viewmodels/dashboard/DashboardViewModel";

import {
  ACTIVITY_TABS,
  INVENTORY_COLOR_BY_KEY,
  NA_MONEY,
  ROW_LINK_FOCUS,
  WEIGHT_STORAGE_KEY,
  activityTabCount,
  creditReturnNavPath,
  formatActivityProductionDate,
  formatCompactKg,
  formatInt,
  formatInvoicePiecesCell,
  formatInvoiceTotalUsdCell,
  formatInvoiceWeightCell,
  formatOrderShipmentStatus,
  formatShipmentOrderTotal,
  formatSummaryMoney,
  newTabRowHandlers,
  pickPackagingVolumeKpi,
  pickProductionCostKpi,
  pickReceivingKpi,
  pickShippedSalesKpi,
  productionAmount,
  readStoredWeightUnit,
  receivingAmount,
  shippedAllocatedAmount,
  sumBucketsPhysical,
  topShippedSalesBucketsCadPrimary,
} from "./dashboardHelpers";
import type {
  ActivityRow,
  DashboardAuthorizedProps,
  WeightUnit,
} from "./dashboardTypes";

import "./dashboard.css";

function KpiMainValueSkeleton({ width = "10rem" }: { width?: string }) {
  return (
    <Skeleton
      width={width}
      height="2.125rem"
      borderRadius="10px"
      className="block max-w-full"
    />
  );
}

function KpiMiniValueSkeleton() {
  return (
    <Skeleton width="4rem" height="1rem" borderRadius="6px" className="block" />
  );
}

function InventoryLegendSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4].map((k) => (
        <div key={k} className="rounded-2xl bg-slate-50 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Skeleton shape="circle" size="0.75rem" />
              <Skeleton width="8rem" height="0.875rem" borderRadius="6px" />
            </div>
            <Skeleton width="2.5rem" height="0.875rem" borderRadius="6px" />
          </div>
          <Skeleton
            width="100%"
            height="0.375rem"
            borderRadius="999px"
            className="mt-3 block"
          />
          <Skeleton
            width="75%"
            height="0.75rem"
            borderRadius="6px"
            className="mt-2 block"
          />
        </div>
      ))}
    </div>
  );
}

function TableSkeletonRows({
  cols,
  rows = 6,
}: {
  cols: number;
  rows?: number;
}) {
  return Array.from({ length: rows }, (_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }, (_, j) => (
        <td key={j} className="px-5 py-3 align-middle">
          <Skeleton
            height="0.875rem"
            borderRadius="6px"
            width={
              j === 0
                ? "72%"
                : j === cols - 1
                  ? cols >= 5
                    ? "42%"
                    : "48%"
                  : "88%"
            }
          />
        </td>
      ))}
    </tr>
  ));
}

function DashboardAuthorized({ logout }: DashboardAuthorizedProps) {

  const { summary, activity, loading, fetchError, refresh } =
    useDashboardViewModel(logout);

  const [fromDate, setFromDate] = useState(() =>
    format(subDays(new Date(), 29), "yyyy-MM-dd"),
  );
  const [toDate, setToDate] = useState(() => format(new Date(), "yyyy-MM-dd"));

  const [weightUnit, setWeightUnitState] = useState(readStoredWeightUnit);
  const [activityTab, setActivityTab] = useState<string>("recent-invoices");

  const rangeError = useMemo(() => {
    const from = parseISO(fromDate);
    const to = parseISO(toDate);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
      return "Enter valid dates.";
    }
    if (to < from) {
      return "End date must be on or after start date.";
    }
    if (differenceInCalendarDays(to, from) > 366) {
      return "Date range must not exceed 366 days.";
    }
    return null;
  }, [fromDate, toDate]);

  useEffect(() => {
    if (rangeError) return;
    refresh(fromDate, toDate);
  }, [fromDate, toDate, rangeError, refresh]);

  function setWeightUnit(unit: WeightUnit) {
    const u: WeightUnit = unit === "lbs" ? "lbs" : "kg";
    localStorage.setItem(WEIGHT_STORAGE_KEY, u === "lbs" ? "lbs" : "kg");
    setWeightUnitState(u);
  }

  const kpis = summary?.kpis;
  const receivingKpi = pickReceivingKpi(kpis);
  const productionCostKpi = pickProductionCostKpi(kpis);
  const packagingVolumeKpi = pickPackagingVolumeKpi(kpis);
  const shippedSalesKpi = pickShippedSalesKpi(kpis);
  const shippedSalesTopBuckets = topShippedSalesBucketsCadPrimary(
    shippedSalesKpi?.by_currency,
  );
  const productionPhysicalTotals = sumBucketsPhysical(
    productionCostKpi?.by_currency,
    productionCostKpi,
  );
  const shippedPhysicalTotals = sumBucketsPhysical(
    shippedSalesKpi?.by_currency,
    shippedSalesKpi,
  );
  const inv = summary?.inventory_by_status;
  const weightKg = weightUnit !== "lbs";

  const throughputTotals = useMemo(() => {
    const rows = summary?.throughput_daily;
    if (!rows?.length) {
      return { receiving: 0, production: 0, shipped: 0 };
    }
    return rows.reduce(
      (acc, d) => ({
        receiving: acc.receiving + (Number(d.receiving_kg) || 0),
        production: acc.production + (Number(d.production_kg) || 0),
        shipped: acc.shipped + (Number(d.shipped_kg) || 0),
      }),
      { receiving: 0, production: 0, shipped: 0 },
    );
  }, [summary?.throughput_daily]);

  const inventoryChartData = useMemo(() => {
    const grouped = inv?.grouped ?? [];
    if (!grouped.length) {
      return { labels: [], datasets: [{ data: [], backgroundColor: [] }] };
    }
    return {
      labels: grouped.map((r) => r.label),
      datasets: [
        {
          data: grouped.map((r) => Number(r.count) || 0),
          backgroundColor: grouped.map(
            (r) => INVENTORY_COLOR_BY_KEY[r.key] || "#94a3b8",
          ),
          borderWidth: 0,
        },
      ],
    };
  }, [inv?.grouped]);

  const inventoryChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "58%",
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label(ctx: {
              parsed?: number;
              label?: string;
              dataset?: { data?: number[] };
            }) {
              const raw = Number(ctx.parsed) || 0;
              const arr = ctx.dataset?.data ?? [];
              const total = arr.reduce(
                (a: number, b: number) => a + Number(b),
                0,
              );
              const pct = total > 0 ? ((raw / total) * 100).toFixed(2) : "0";
              const label = ctx.label ?? "";
              return ` ${label}: ${raw.toLocaleString()} (${pct}%)`;
            },
          },
        },
      },
    }),
    [],
  );

  const throughputChartData = useMemo(() => {
    const daily = summary?.throughput_daily ?? [];
    const labels = daily.map((d) => {
      try {
        return format(parseISO(d.date), "MMM d");
      } catch {
        return d.date;
      }
    });
    return {
      labels,
      datasets: [
        {
          label: "Receiving",
          data: daily.map((d) => Number(d.receiving_kg) || 0),
          borderColor: "#3b82f6",
          backgroundColor: "rgba(59, 130, 246, 0.08)",
          fill: true,
          tension: 0.35,
          borderWidth: 2,
          pointRadius: 3,
          pointBackgroundColor: "#3b82f6",
        },
        {
          label: "Production",
          data: daily.map((d) => Number(d.production_kg) || 0),
          borderColor: "#10b981",
          backgroundColor: "rgba(16, 185, 129, 0.1)",
          fill: true,
          tension: 0.35,
          borderWidth: 2,
          pointRadius: 3,
          pointBackgroundColor: "#10b981",
        },
        {
          label: "Shipped",
          data: daily.map((d) => Number(d.shipped_kg) || 0),
          borderColor: "#8b5cf6",
          backgroundColor: "rgba(139, 92, 246, 0.08)",
          fill: true,
          tension: 0.35,
          borderWidth: 2,
          pointRadius: 3,
          pointBackgroundColor: "#8b5cf6",
        },
      ],
    };
  }, [summary?.throughput_daily]);

  const throughputChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "top",
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            color: "#475569",
          },
        },
      },
      scales: {
        x: {
          grid: { color: "rgba(226, 232, 240, 0.9)" },
          ticks: { color: "#64748b", maxRotation: 45 },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(226, 232, 240, 0.9)" },
          ticks: {
            color: "#64748b",
            callback(value: string | number) {
              const n = Number(value);
              if (n >= 1000) return `${(n / 1000).toFixed(0)}k`;
              return n;
            },
          },
        },
      },
    }),
    [],
  );

  const counts = activity?.counts ?? {};
  const activityRowsPerTab = activity?.meta?.rows_per_tab ?? 5;

  const recentInvoiceRows =
    activity?.recent_invoices ?? activity?.recent_receiving ?? [];
  const recentProductionRows =
    activity?.recent_productions ?? activity?.recent_production ?? [];

  const metaRangeLabel = summary?.meta
    ? `${summary.meta.from} → ${summary.meta.to}`
    : "";

  return (
    <main
      className="min-h-screen bg-slate-100 p-4 text-slate-950 antialiased sm:p-6 lg:p-8"
      aria-busy={loading}
    >
      <div className="mx-auto space-y-6">
        <header className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              Operations dashboard
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Receiving, manufacturing, and shipping for the selected period.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* <div
              className="inline-flex rounded-xl border border-slate-200 bg-slate-100 p-0.5 shadow-inner"
              role="group"
              aria-label="Weight display unit"
            >
              <button
                type="button"
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 ${
                  weightKg
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                }`}
                aria-pressed={weightKg}
                onClick={() => setWeightUnit("kg")}
              >
                kg
              </button>
              <button
                type="button"
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 ${
                  !weightKg
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                }`}
                aria-pressed={!weightKg}
                onClick={() => setWeightUnit("lbs")}
              >
                lbs
              </button>
            </div> */}
            <label className="sr-only" htmlFor="dashboard-range-from">
              From
            </label>
            <input
              id="dashboard-range-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm"
            />
            <span className="text-slate-400">—</span>
            <label className="sr-only" htmlFor="dashboard-range-to">
              To
            </label>
            <input
              id="dashboard-range-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm"
            />
          </div>
        </header>

        {(rangeError || fetchError) && (
          <div
            className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
            role="alert"
          >
            {rangeError ?? fetchError}
          </div>
        )}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {/* Receiving spend */}
          <button
            type="button"
            className="compact-kpi-card group text-left"
            disabled={loading}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="compact-kpi-icon bg-blue-50 text-blue-600">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </span>
                <div className="min-w-0">
                  <p className="compact-kpi-label">Receiving spend</p>
                  <p className="compact-kpi-context">Inbound material cost</p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-end justify-between gap-3">
              <div>
                <p className="compact-kpi-value">
                  {loading ? (
                    <KpiMainValueSkeleton />
                  ) : (
                    formatSummaryMoney(
                      receivingAmount(receivingKpi),
                      receivingKpi,
                      2,
                    )
                  )}
                </p>
                <p className="compact-kpi-subtitle">Total received cost</p>
              </div>
              <span className="compact-kpi-arrow">→</span>
            </div>
            <div className="compact-kpi-footer">
              <div>
                <p className="compact-kpi-mini-label">
                  {weightKg ? "Kg received" : "Lbs received"}
                </p>
                <p className="compact-kpi-mini-value">
                  {loading ? (
                    <KpiMiniValueSkeleton />
                  ) : (
                    formatInt(
                      weightKg
                        ? receivingKpi?.weight_kg
                        : receivingKpi?.weight_lbs,
                    )
                  )}
                </p>
              </div>
            </div>
          </button>

          {/* Production output */}
          <button
            type="button"
            className="compact-kpi-card group text-left"
            disabled={loading}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="compact-kpi-icon bg-emerald-50 text-emerald-600">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 21V9l5 4V9l5 4V5h6v16z"
                    />
                  </svg>
                </span>
                <div className="min-w-0">
                  <p className="compact-kpi-label">Production output</p>
                  <p className="compact-kpi-context">Manufactured volume</p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-end justify-between gap-3">
              <div>
                <p className="compact-kpi-value">
                  {loading ? (
                    <KpiMainValueSkeleton width="8rem" />
                  ) : (
                    <>
                      <span>
                        {formatInt(
                          weightKg
                            ? kpis?.manufactured_volume?.weight_kg
                            : kpis?.manufactured_volume?.weight_lbs,
                        )}
                      </span>
                      <span className="compact-kpi-unit">{weightUnit}</span>
                    </>
                  )}
                </p>
                <p className="compact-kpi-subtitle">Net weight produced</p>
              </div>
              <span className="compact-kpi-arrow">→</span>
            </div>
            <div className="compact-kpi-footer">
              <div>
                <p className="compact-kpi-mini-label">Runs</p>
                <p className="compact-kpi-mini-value">
                  {loading ? (
                    <KpiMiniValueSkeleton />
                  ) : (
                    formatInt(kpis?.manufactured_volume?.runs)
                  )}
                </p>
              </div>
            </div>
          </button>

          {/* Production cost */}
          <button
            type="button"
            className="compact-kpi-card group text-left"
            disabled={loading}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="compact-kpi-icon bg-orange-50 text-orange-600">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V6m0 12v-2"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </span>
                <div className="min-w-0">
                  <p className="compact-kpi-label">Packaging volume</p>
                  <p className="compact-kpi-context">Packaging volume (kg)</p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="compact-kpi-value">
                  {loading ? (
                    <KpiMainValueSkeleton width="8rem" />
                  ) : (
                    <>
                      <span>{formatInt(packagingVolumeKpi?.weight_kg)}</span>
                      <span className="compact-kpi-unit">{weightUnit}</span>
                    </>
                  )}
                </p>
                <p className="compact-kpi-subtitle mt-1">Net Weight Packaged</p>
              </div>
              <span className="compact-kpi-arrow shrink-0">→</span>
            </div>
            <div className="compact-kpi-footer">
              <div>
                <p className="compact-kpi-mini-label">Units packaged</p>
                <p className="compact-kpi-mini-value">
                  {loading ? (
                    <KpiMiniValueSkeleton />
                  ) : (
                    formatInt(packagingVolumeKpi?.units)
                  )}
                </p>
              </div>
            </div>
          </button>

          {/* Shipped value */}
          <button
            type="button"
            className="compact-kpi-card group text-left"
            disabled={loading}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="compact-kpi-icon bg-indigo-50 text-indigo-600">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 7h13v10H3zM16 10h3l2 3v4h-5z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M7 20a2 2 0 100-4 2 2 0 000 4zM18 20a2 2 0 100-4 2 2 0 000 4z"
                    />
                  </svg>
                </span>
                <div className="min-w-0">
                  <p className="compact-kpi-label">Shipped value</p>
                  <p className="compact-kpi-context">
                    Cartons shipped or verified
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {loading ? (
                  <div className="space-y-2">
                    <KpiMainValueSkeleton width="11rem" />
                    <Skeleton width="8rem" height="1.5rem" borderRadius="8px" />
                  </div>
                ) : shippedSalesTopBuckets ? (
                  <div className="space-y-1">
                    {shippedSalesTopBuckets.map((bucket, i) => (
                      <p
                        key={`ss-${bucket.currency ?? ""}-${i}`}
                        className={
                          i === 0
                            ? "compact-kpi-value leading-none"
                            : "text-lg font-bold tabular-nums leading-tight text-slate-800"
                        }
                      >
                        {formatSummaryMoney(bucket.allocated, bucket, 2)}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="compact-kpi-value">
                    {formatSummaryMoney(
                      shippedAllocatedAmount(shippedSalesKpi),
                      shippedSalesKpi?.by_currency?.[0] ?? shippedSalesKpi,
                      2,
                    )}
                  </p>
                )}
                <p className="compact-kpi-subtitle mt-1">Allocated sales</p>
              </div>
              <span className="compact-kpi-arrow shrink-0">→</span>
            </div>
            <div className="compact-kpi-footer">
              <div>
                <p className="compact-kpi-mini-label">
                  {weightKg ? "Kg shipped" : "Lbs shipped"}
                </p>
                <p className="compact-kpi-mini-value">
                  {loading ? (
                    <KpiMiniValueSkeleton />
                  ) : (
                    formatInt(
                      weightKg
                        ? shippedPhysicalTotals.weight_kg
                        : shippedPhysicalTotals.weight_lbs,
                    )
                  )}
                </p>
              </div>
            </div>
          </button>
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.1fr_1.1fr]">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-soft">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-base font-semibold">
                  Production by status
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Snapshot of production by status. Percentages are counts vs
                  total production.
                </p>
              </div>
              <Link
                to="/manufactured-products"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
              >
                View production
                <span aria-hidden className="text-slate-400">
                  →
                </span>
              </Link>
            </div>

            <div className="mt-8 grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div className="flex items-center justify-center">
                <div className="relative mx-auto h-[260px] w-[260px] max-w-full">
                  {loading && !((inv?.grouped?.length ?? 0) > 0) ? (
                    <div className="flex h-full items-center justify-center">
                      <Skeleton shape="circle" size="220px" />
                    </div>
                  ) : !((inv?.grouped?.length ?? 0) > 0) ? (
                    <p className="flex h-full items-center justify-center text-sm text-slate-500">
                      No inventory breakdown.
                    </p>
                  ) : (
                    <>
                      <Chart
                        type="doughnut"
                        data={inventoryChartData}
                        options={inventoryChartOptions}
                        className="h-full w-full max-h-[260px]"
                      />
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <p className="text-4xl font-bold tabular-nums text-slate-900">
                          {formatInt(inv?.total_rows)}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {loading && !((inv?.grouped?.length ?? 0) > 0) ? (
                  <InventoryLegendSkeleton />
                ) : (
                  (inv?.grouped ?? []).map((row) => {
                    const color = INVENTORY_COLOR_BY_KEY[row.key] || "#94a3b8";
                    const pct = Math.min(
                      100,
                      Math.max(0, Number(row.pct_of_total) || 0),
                    );
                    const statuses = Array.isArray(row.statuses)
                      ? row.statuses.join(", ")
                      : "";
                    return (
                      <div
                        key={row.key}
                        className="rounded-2xl bg-slate-50 p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                            <span className="text-sm font-medium text-slate-700">
                              {row.label}
                            </span>
                          </div>
                          <span className="text-sm font-bold tabular-nums">
                            {formatInt(row.count)}
                          </span>
                        </div>
                        <ProgressBar
                          value={pct}
                          color={color}
                          showValue={false}
                          className="dashboard-inventory-progress mt-3 w-full"
                        />
                        <p className="mt-2 text-xs text-slate-400">
                          {pct.toFixed(2)}% of {formatInt(inv?.total_rows)} ·
                          status {statuses}
                        </p>
                      </div>
                    );
                  })
                )}
                {!loading && !((inv?.grouped?.length ?? 0) > 0) && (
                  <p className="text-sm text-slate-500">
                    No inventory breakdown.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-soft">
            <div className="flex items-start justify-between gap-4 p-5">
              <div>
                <h2 className="text-base font-semibold">Recent shipments</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Unique orders — allocated line value and currency per order
                  (same basis as shipped KPI).
                </p>
              </div>
              <Link
                to="/shipments"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-slate-600 hover:text-slate-950"
              >
                View all →
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-y border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Order</th>
                    <th className="px-5 py-3">Customer</th>
                    <th className="px-5 py-3 text-right">Total</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Shipped</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading && <TableSkeletonRows cols={5} rows={5} />}
                  {!loading &&
                    (summary?.recent_shipments ?? []).map((row) => {
                      const shipPath = `/shipments/${row.order_id}`;
                      return (
                        <tr
                          key={`${row.order_id}-${row.shipped_date ?? row.shipped_at}`}
                          className={`cursor-pointer hover:bg-slate-50 ${ROW_LINK_FOCUS}`}
                          {...newTabRowHandlers(
                            shipPath,
                            `Open shipment ${row.order_number ?? row.order_id}`,
                          )}
                        >
                          <td className="px-5 py-4 font-semibold text-slate-900">
                            {row.order_number ?? `#${row.order_id}`}
                          </td>
                          <td className="px-5 py-4 text-slate-700">
                            {row.customer_name ?? "—"}
                          </td>
                          <td className="px-5 py-4 text-right font-semibold tabular-nums text-slate-900">
                            {formatShipmentOrderTotal(row)}
                          </td>
                          <td
                            className="px-5 py-4 text-slate-600"
                            title={
                              row.status_code != null
                                ? `Status code: ${row.status_code}`
                                : undefined
                            }
                          >
                            {formatOrderShipmentStatus(row)}
                          </td>
                          <td className="px-5 py-4 text-slate-500">
                            {row.shipped_date ?? row.shipped_at ?? "—"}
                          </td>
                        </tr>
                      );
                    })}
                  {!loading && !((summary?.recent_shipments?.length ?? 0) > 0) && (
                    <tr>
                      <td colSpan={5} className="px-5 py-6 text-slate-500">
                        No shipments in range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-soft sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                Throughput (daily)
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Receiving, production, and shipped volume for the selected date
                range.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs sm:min-w-[26rem]">
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-1.5 text-slate-500">
                  <span className="h-2 w-2 rounded-full bg-blue-500" />
                  Receiving
                </div>
                <p className="mt-1 font-bold tabular-nums text-slate-900">
                  {loading ? (
                    <Skeleton
                      width="4.5rem"
                      height="1.25rem"
                      borderRadius="6px"
                    />
                  ) : (
                    formatCompactKg(throughputTotals.receiving)
                  )}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-1.5 text-slate-500">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Production
                </div>
                <p className="mt-1 font-bold tabular-nums text-slate-900">
                  {loading ? (
                    <Skeleton
                      width="4.5rem"
                      height="1.25rem"
                      borderRadius="6px"
                    />
                  ) : (
                    formatCompactKg(throughputTotals.production)
                  )}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-1.5 text-slate-500">
                  <span className="h-2 w-2 rounded-full bg-violet-500" />
                  Shipped
                </div>
                <p className="mt-1 font-bold tabular-nums text-slate-900">
                  {loading ? (
                    <Skeleton
                      width="4.5rem"
                      height="1.25rem"
                      borderRadius="6px"
                    />
                  ) : (
                    formatCompactKg(throughputTotals.shipped)
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-5 overflow-hidden rounded-3xl border border-slate-100 bg-gradient-to-b from-slate-50 to-white p-3 sm:p-4">
            <div className="min-h-[320px] w-full overflow-x-auto">
              {loading && !((summary?.throughput_daily?.length ?? 0) > 0) ? (
                <div className="py-2">
                  <Skeleton width="100%" height="330px" borderRadius="16px" />
                </div>
              ) : !((summary?.throughput_daily?.length ?? 0) > 0) ? (
                <p className="py-10 text-center text-sm text-slate-500">
                  No throughput data for this range.
                </p>
              ) : (
                <Chart
                  type="line"
                  data={throughputChartData}
                  options={throughputChartOptions}
                  className="h-[330px] min-w-[min(100%,760px)] w-full"
                />
              )}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
            <p>Daily kg by Eastern calendar day for the selected range.</p>
            <p className="tabular-nums">{metaRangeLabel}</p>
          </div>
        </section>

        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-soft">
          <div className="flex flex-col gap-4 border-b border-slate-200 p-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12h6m-7 4h8M6 4h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2z"
                  />
                </svg>
              </span>
              <div>
                <h2 className="text-base font-semibold text-slate-900">
                  Activity Center
                </h2>
                <p className="mt-0.5 text-sm text-slate-500">
                  Recent activity and exceptions in one place.
                </p>
              </div>
            </div>
            <Link
              to="/receiving"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 shadow-sm hover:bg-slate-50"
            >
              View full reports →
            </Link>
          </div>

          <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-3">
            <div
              className="flex gap-2 overflow-x-auto pb-1"
              role="tablist"
              aria-label="Activity Center tabs"
            >
              {ACTIVITY_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activityTab === tab.id}
                  className="action-tab"
                  onClick={() => setActivityTab(tab.id)}
                >
                  {tab.label}{" "}
                  <span
                    className={`tab-count ${tab.badge === "alert" ? "alert" : ""} ${tab.badge === "warning" ? "warning" : ""}`}
                  >
                    {loading ? (
                      <Skeleton
                        width="1.6rem"
                        height="0.8rem"
                        borderRadius="6px"
                        className="inline-block align-middle"
                      />
                    ) : (
                      formatInt(activityTabCount(counts, tab))
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-5">
            {activityTab === "recent-invoices" && (
              <div>
                <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Recent invoices
                    </h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Supplier, invoice totals — weight, pieces, and cost.
                    </p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">
                    Top {activityRowsPerTab}
                  </span>
                </div>
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="w-full min-w-[52rem] text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-5 py-3">Supplier</th>
                        <th className="px-5 py-3">Invoice</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3 text-right">Weight</th>
                        <th className="px-5 py-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {loading && <TableSkeletonRows cols={6} rows={6} />}
                      {!loading &&
                        recentInvoiceRows.map((row, i) => {
                          const recvPath =
                            row.id != null ? `/receiving/${row.id}` : null;
                          return (
                            <tr
                              key={`${row.id ?? row.invoice_number}-${i}`}
                              className={
                                recvPath
                                  ? `cursor-pointer hover:bg-slate-50 ${ROW_LINK_FOCUS}`
                                  : "hover:bg-slate-50"
                              }
                              {...newTabRowHandlers(
                                recvPath,
                                recvPath
                                  ? `Open receiving invoice ${row.invoice_number}`
                                  : undefined,
                              )}
                            >
                              <td className="px-5 py-4 text-slate-800">
                                {row.supplier_name ?? row.product_name ?? "—"}
                              </td>
                              <td className="px-5 py-4">
                                <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-900">
                                  {row.invoice_number}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-slate-700">
                                {row.status ?? "—"}
                              </td>
                              <td className="px-5 py-4 text-right tabular-nums text-slate-600">
                                {formatInvoiceWeightCell(row, weightKg)}
                              </td>

                              <td className="px-5 py-4 text-right font-semibold tabular-nums">
                                ${row.total_cost}
                              </td>
                            </tr>
                          );
                        })}
                      {!loading && !(recentInvoiceRows.length > 0) && (
                        <tr>
                          <td colSpan={6} className="px-5 py-6 text-slate-500">
                            No rows.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activityTab === "recent-production" && (
              <div>
                <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Recent production
                    </h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Latest manufactured-product runs (up to top{" "}
                      {activityRowsPerTab} in the selected date range).
                    </p>
                  </div>
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                    Top {activityRowsPerTab}
                  </span>
                </div>
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="w-full min-w-[36rem] text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-5 py-3">Lot</th>
                        <th className="px-5 py-3">Product</th>
                        <th className="px-5 py-3">Production date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {loading && <TableSkeletonRows cols={3} rows={6} />}
                      {!loading &&
                        recentProductionRows.map((row, i) => {
                          const productPath =
                            row.id != null
                              ? `/manufactured-products/${row.id}`
                              : null;
                          const lotLabel = row.lot_number ?? "—";
                          return (
                            <tr
                              key={`${row.id ?? row.lot_number}-${i}`}
                              className={
                                productPath
                                  ? `cursor-pointer hover:bg-slate-50 ${ROW_LINK_FOCUS}`
                                  : "hover:bg-slate-50"
                              }
                              {...newTabRowHandlers(
                                productPath,
                                productPath
                                  ? `Open manufactured product ${lotLabel}`
                                  : undefined,
                              )}
                            >
                              <td className="px-5 py-4">
                                <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">
                                  {lotLabel}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-slate-700">
                                {row.product_name ?? "—"}
                              </td>
                              <td className="px-5 py-4 tabular-nums text-slate-600">
                                {formatActivityProductionDate(
                                  row.production_date,
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      {!loading && !(recentProductionRows.length > 0) && (
                        <tr>
                          <td colSpan={3} className="px-5 py-6 text-slate-500">
                            No rows.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

export default function Dashboard(props: DashboardAuthorizedProps) {
  const raw =
    typeof window !== "undefined" ? localStorage.getItem("user_type") : null;
  const t = parseInt(String(raw ?? ""), 10);
  if (t !== 1) {
    return (
      <main className="min-h-screen bg-slate-100 p-4 text-slate-950 antialiased sm:p-6 lg:p-8" />
    );
  }
  return <DashboardAuthorized {...props} />;
}
